// DTOs/schemas do módulo "campanhas" (listas de contatos, campanhas, relatório). Pertence ao pacote campanhas.
import { z } from "zod";

import type { CategoriaMeta, StatusCampanha } from "./enums";

// ─── Limites ─────────────────────────────────────────────────────────────────

/** Limites do upload de lista (CSV ou JSON). Acima disso a importação é recusada. */
export const LISTA_MAX_LINHAS = 50_000;
export const LISTA_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Máximo de colunas/campos extras por lista (união de todos os contatos) e por contato.
 * Sem teto, N contatos com chaves distintas custavam N×N no armazenamento (DoS).
 */
export const LISTA_MAX_COLUNAS = 20;

/** Quantas linhas rejeitadas ficam guardadas na lista para exibição. */
export const LISTA_MAX_REJEITADAS_GUARDADAS = 5_000;

/** Máximo de tentativas de envio por contato (erros transitórios). */
export const CAMPANHA_MAX_TENTATIVAS = 3;

/** Janela e limiar da pausa automática (opcional, desligada por padrão). */
export const PAUSA_AUTOMATICA_JANELA = 200;
export const PAUSA_AUTOMATICA_LIMITE_FALHA = 0.2;
/** Menor amostra que a pausa automática considera (evita pausar com 2 falhas em 5). */
export const PAUSA_AUTOMATICA_AMOSTRA_MINIMA = 50;

// ─── Listas de contatos ──────────────────────────────────────────────────────

export interface LinhaRejeitada {
  /** Linha no arquivo (cabeçalho = 1) ou posição (1-based) no JSON. */
  linha: number;
  /** Telefone como veio no arquivo. */
  telefone: string;
  motivo: string;
}

export interface ListaResumo {
  /** publicId */
  id: string;
  nome: string;
  totalContatos: number;
  totalRejeitadas: number;
  /** Linhas descartadas por telefone repetido (a primeira ocorrência fica). */
  totalDuplicadas: number;
  /** Colunas disponíveis como variáveis (campos extras do arquivo). */
  campos: string[];
  criadoEm: string;
}

export interface ContatoAmostra {
  telefone: string;
  nome: string | null;
  campos: Record<string, string>;
}

export interface ListaDetalhe extends ListaResumo {
  amostra: ContatoAmostra[];
  rejeitadas: LinhaRejeitada[];
  /** true quando há mais rejeitadas do que as guardadas. */
  rejeitadasTruncadas: boolean;
}

const telefoneBruto = z.union([z.string(), z.number()]).transform((v) => String(v));

/** `POST /listas/json` — importação pela API. Campos extras em `campos` viram variáveis. */
export const zCriarListaJson = z.object({
  nome: z.string({ required_error: "Informe o nome da lista." }).trim().min(1, "Informe o nome da lista.").max(120),
  contatos: z
    .array(
      z.object({
        telefone: telefoneBruto,
        nome: z.string().trim().max(200).nullish(),
        campos: z
          .record(z.string().trim().min(1).max(60), z.union([z.string(), z.number(), z.boolean()]).transform(String))
          .refine((c) => Object.keys(c).length <= LISTA_MAX_COLUNAS, {
            message: `Cada contato pode ter no máximo ${LISTA_MAX_COLUNAS} campos extras.`,
          })
          .optional(),
      }),
      { required_error: "Informe a lista de contatos." },
    )
    .min(1, "Informe ao menos um contato.")
    .max(LISTA_MAX_LINHAS, `No máximo ${LISTA_MAX_LINHAS.toLocaleString("pt-BR")} contatos por lista.`),
});
export type CriarListaJson = z.infer<typeof zCriarListaJson>;

// ─── Agendamento ─────────────────────────────────────────────────────────────

const RE_DATA_HORA =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Interpreta o agendamento. Sem fuso explícito (`2026-10-15T09:00`) vale o horário
 * de São Paulo (UTC-03:00; o Brasil não tem horário de verão desde 2019). Com
 * `Z` ou `±HH:MM` respeita o que veio. Devolve null se não for uma data válida.
 */
export function parseAgendamentoSaoPaulo(valor: string): Date | null {
  const m = RE_DATA_HORA.exec(valor.trim());
  if (!m) return null;
  const [, ano, mes, dia, hora, min, seg, fuso] = m;
  const h = Number(hora);
  const mi = Number(min);
  const s = Number(seg ?? "0");
  if (h > 23 || mi > 59 || s > 59) return null;
  let desvio = "-03:00";
  if (fuso) {
    desvio = fuso.toUpperCase() === "Z" ? "Z" : fuso.length === 5 ? `${fuso.slice(0, 3)}:${fuso.slice(3)}` : fuso;
  }
  const d = new Date(`${ano}-${mes}-${dia}T${hora}:${min}:${(seg ?? "00").padStart(2, "0")}${desvio}`);
  if (Number.isNaN(d.getTime())) return null;
  // Rejeita 31/02 e afins (o Date "rola" o mês).
  const conferencia = new Date(`${ano}-${mes}-${dia}T00:00:00Z`);
  if (conferencia.getUTCDate() !== Number(dia)) return null;
  return d;
}

// ─── Campanhas ───────────────────────────────────────────────────────────────

/** Valor de uma variável do template: campo da lista ou texto fixo. */
export const zMapeamentoVariavel = z.discriminatedUnion("tipo", [
  z.object({
    tipo: z.literal("campo"),
    valor: z.string().trim().min(1, "Escolha o campo da lista.").max(100),
  }),
  z.object({
    tipo: z.literal("fixo"),
    valor: z.string().trim().min(1, "Informe o valor fixo.").max(1000),
  }),
]);
export type MapeamentoVariavel = z.infer<typeof zMapeamentoVariavel>;

/** Chave = posição da variável no template (`"1"`, `"2"`…, na ordem cabeçalho, corpo, botões). */
export const zMapeamento = z.record(z.string().regex(/^[1-9]\d{0,2}$/, "Posição de variável inválida."), zMapeamentoVariavel);
export type Mapeamento = z.infer<typeof zMapeamento>;

const nomeCampanha = z.string({ required_error: "Informe o nome da campanha." }).trim().min(1, "Informe o nome da campanha.").max(120);
const publicIdCampo = (rotulo: string) =>
  z.string({ required_error: `Informe ${rotulo}.` }).trim().min(1, `Informe ${rotulo}.`).max(64);
const agendamento = z
  .string()
  .trim()
  .refine((v) => parseAgendamentoSaoPaulo(v) !== null, "Data/hora inválida. Use AAAA-MM-DDTHH:mm (horário de Brasília).");

/** `POST /campanhas`. `agendadaPara` sem fuso = America/Sao_Paulo. */
export const zCriarCampanha = z.object({
  nome: nomeCampanha,
  /** publicId do número OFICIAL. */
  numeroId: publicIdCampo("o número"),
  /** publicId do template (do próprio número). */
  templateId: publicIdCampo("o template"),
  /** publicId da lista de contatos. */
  listaId: publicIdCampo("a lista de contatos"),
  mapeamento: zMapeamento.default({}),
  agendadaPara: agendamento.optional(),
  /** Padrão false: o ZapHub não bloqueia o número oficial. */
  pausaAutomatica: z.boolean().default(false),
  /** true = dispara agora (ignorado se houver `agendadaPara`). */
  iniciar: z.boolean().default(false),
});
export type CriarCampanha = z.output<typeof zCriarCampanha>;

/**
 * `PATCH /campanhas/:id`. Rascunho/agendada aceitam tudo; depois de iniciada só
 * `nome` e `pausaAutomatica` mudam. `agendadaPara: null` remove o agendamento.
 */
export const zAtualizarCampanha = z
  .object({
    nome: nomeCampanha.optional(),
    templateId: publicIdCampo("o template").optional(),
    listaId: publicIdCampo("a lista de contatos").optional(),
    mapeamento: zMapeamento.optional(),
    agendadaPara: agendamento.nullable().optional(),
    pausaAutomatica: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "Informe ao menos um campo para alterar." });
export type AtualizarCampanha = z.infer<typeof zAtualizarCampanha>;

/** `POST /campanhas/previa` — mapeamento pode estar incompleto (a tela mostra `{{n}}` nas faltantes). */
export const zPreviaCampanha = z.object({
  numeroId: publicIdCampo("o número"),
  templateId: publicIdCampo("o template"),
  listaId: publicIdCampo("a lista de contatos"),
  mapeamento: zMapeamento.default({}),
});
export type PreviaCampanhaEntrada = z.output<typeof zPreviaCampanha>;

export interface MensagemRenderizada {
  cabecalho: string | null;
  /** Tipo da mídia quando o cabeçalho é imagem/vídeo/documento. */
  cabecalhoMidia: "IMAGE" | "VIDEO" | "DOCUMENT" | null;
  corpo: string;
  rodape: string | null;
  botoes: Array<{ tipo: string; texto: string }>;
}

export interface EstimativaPais {
  /** DDI ("55") ou "*" quando a tabela usou "Demais países". */
  ddi: string;
  rotulo: string;
  quantidade: number;
  /** USD por mensagem (tabela vigente). */
  precoUsd: string;
  centavosPorMensagem: number;
  centavos: number;
}

export interface EstimativaCusto {
  categoria: CategoriaMeta;
  /** Contatos que receberão (exclui opt-out). */
  quantidade: number;
  ignoradosOptout: number;
  centavos: number;
  porPais: EstimativaPais[];
  /** USD→BRL usado; null se a plataforma não tem câmbio. */
  cambio: string | null;
  tabelaVersao: number | null;
  /** Países sem preço na tabela (não entram no total). */
  semPreco: string[];
  /** Sempre true: é estimativa pela tabela pública, não a fatura da Meta. */
  estimado: true;
}

export type AlertaCampanhaCodigo =
  | "numero_aguardando_webhook"
  | "numero_restrito"
  | "numero_desativado"
  | "pagamento_meta"
  | "qualidade_baixa"
  | "template_nao_aprovado";

/** Informativo: NUNCA bloqueia a criação nem o disparo (o produto libera o número oficial). */
export interface AlertaCampanha {
  codigo: AlertaCampanhaCodigo;
  mensagem: string;
}

export interface PreviaCampanha {
  contato: { telefone: string; nome: string | null };
  /** Valores resolvidos por posição (vazio onde faltou mapeamento). */
  variaveis: string[];
  /** Posições (1-based) sem mapeamento. */
  semMapeamento: number[];
  mensagem: MensagemRenderizada;
  estimativa: EstimativaCusto;
  alertas: AlertaCampanha[];
}

export interface TotaisEnvios {
  pendente: number;
  enviado: number;
  entregue: number;
  lido: number;
  respondido: number;
  falhou: number;
  ignoradoOptout: number;
}

export interface ErroFrequente {
  codigo: string;
  mensagem: string;
  quantidade: number;
}

export interface CampanhaResumo {
  /** publicId */
  id: string;
  nome: string;
  status: StatusCampanha;
  numero: { id: string; apelido: string };
  template: { id: string; nome: string };
  lista: { id: string; nome: string } | null;
  agendadaPara: string | null;
  iniciadaEm: string | null;
  concluidaEm: string | null;
  pausaAutomatica: boolean;
  motivoPausa: string | null;
  custoEstimadoCentavos: number;
  total: number;
  totais: TotaisEnvios;
  criadoEm: string;
}

export interface CampanhaDetalhe extends CampanhaResumo {
  numero: { id: string; apelido: string; telefone: string | null; status: string; qualityRating: string | null };
  template: { id: string; nome: string; idioma: string; categoria: CategoriaMeta; status: string };
  lista: { id: string; nome: string; totalContatos: number } | null;
  mapeamento: Mapeamento;
  alertas: AlertaCampanha[];
}

export interface CampanhaCriada extends CampanhaDetalhe {
  estimativa: EstimativaCusto;
}

export interface RelatorioCampanha {
  id: string;
  nome: string;
  status: StatusCampanha;
  motivoPausa: string | null;
  pausaAutomatica: boolean;
  /** Contatos da campanha (todos os status). */
  total: number;
  /** Quantidade por status ATUAL do envio (cada contato conta uma vez). */
  totais: TotaisEnvios;
  /** Percentual (0–100, 1 casa) de cada status sobre `total`. */
  percentuais: TotaisEnvios;
  /** Funil acumulado: quem chegou pelo menos até cada etapa. */
  funil: { enviadas: number; entregues: number; lidas: number; respondidas: number };
  /** Falhas ÷ (enviados + falhas), em %. Contatos ignorados/pendentes não entram. */
  taxaFalha: number;
  erroMaisFrequente: ErroFrequente | null;
  custoEstimadoCentavos: number;
  leads: { captados: number; repassados: number; responderam: number };
  atualizadoEm: string;
}

/** Dados do evento de saída `campanha.status` e do SSE `campanha.progresso` (ids públicos). */
export interface CampanhaStatusEvento {
  campanhaId: string;
  nome: string;
  numeroId: string;
  status: StatusCampanha;
  motivo: string | null;
  total: number;
  totais: TotaisEnvios;
  taxaFalha: number;
  erroMaisFrequente: ErroFrequente | null;
  custoEstimadoCentavos: number;
  ocorridoEm: string;
}

/** Resultado de `CampanhasService.marcarRespondido` (para o inbox/leads). */
export interface ResultadoMarcarRespondido {
  /** Existe um envio de campanha com esse wamid. */
  encontrado: boolean;
  /** true só quando ESTA chamada mudou o envio para RESPONDIDO. */
  marcado: boolean;
  /** `Campanha.id` interno (para `Lead.campanhaId`); null se não encontrado. */
  campanhaId: bigint | null;
  /** `Campanha.publicId`. */
  campanhaPublicId: string | null;
}

/** Espelha o enum `StatusEnvioCampanha` do Prisma. */
export const STATUS_ENVIO_CAMPANHA = [
  "PENDENTE",
  "ENVIADO",
  "ENTREGUE",
  "LIDO",
  "RESPONDIDO",
  "FALHOU",
  "IGNORADO_OPTOUT",
] as const;
export type StatusEnvioCampanha = (typeof STATUS_ENVIO_CAMPANHA)[number];
